import useSWR from "swr";
import VirtualTable from "../../custom/VirtualTable";
import { customFetcher, extractReactTableData, fileDownload } from "../../../utils";
import { IFFM } from "../../../globals/interfaces";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import apiClient from "../../../apiClient";
import useUnit from "../../../customHooks/useUnit";
import styled from "styled-components";
import Card from "../../custom/Card";
import useFlightsData from "./useFlightsData";
import { Input } from "reactstrap";
import useLoading from "../../../customHooks/useLoading";
import ActionIcon from "../../custom/ActionIcon";

interface ExtendedFFM extends IFFM {
    storageDays: number;
}

export default function PerishableStorage({
    tab
}: {
    tab
}) {

    const tableRef = useRef<HTMLDivElement>(null);
    const s_unit = useUnit();
    const s_destination = s_unit.substring(1, 4);
    const [d_arrival_date, set_d_arrival_date] = useState(moment().format('YYYY-MM-DD'));
    const [s_flight_id, set_s_flight_id] = useState('');
    const [flights, setFlights] = useState([]);
    const key = (moment(d_arrival_date).isValid() && tab === '5') ? `/perishable-storage-ffms?d_arrival_date=${d_arrival_date}&s_destination=${s_destination}&s_unit=${s_unit}` : null;
    const {
        data,
        isLoading
    } = useSWR(key, customFetcher<ExtendedFFM>);
    const renderData = (data || []).filter(item => item.s_flight_id === s_flight_id);

    useEffect(() => {
        const set = new Set((data || []).map(item => item.s_flight_id));
        setFlights(Array.from(set).map(item => ({
            s_flight_number: item.split('/')[0],
            s_flight_id: item
        })));
    }, [data]);

    // @ts-ignore
    // const { flightsArray } = useFlightsData(data || []);
    const { setLoading } = useLoading();

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading]);

    const handleDownload = () => {
        const schema = [{
            key: 's_uld',
            name: 'ULD'
        }, {
            key: 's_mawb',
            name: 'MAWB',
            mawb: true
        }, {
            key: 's_type',
            name: 'Type'
        }, {
            key: 's_destination',
            name: 'DES'
        }, {
            key: 'i_actual_piece_count',
            name: 'Pieces'
        }, {
            key: 's_commodity',
            name: 'Commodity'
        }, {
            key: 's_special_handling_code',
            name: 'Special Handling Codes'
        }, {
            key: 's_modified_by',
            name: 'Modified'
        }, {
            key: 'd_arrival_date',
            name: 'Arrival Date',
            date: true
        }, {
            key: 'd_storage_start',
            name: 'Storage Start',
            date: true
        }, {
            key: 'storageDays',
            name: 'Storage Days',
            number: true
        }];

        const data = extractReactTableData(tableRef, [], schema);
        fileDownload(data, `Perishable Storage`);
    }

    return (
        <Container>
            <FlightsContainer>
                <h4>Date</h4>
                <Input type='date' value={d_arrival_date} onChange={(e) => set_d_arrival_date(e.target.value)} className='mb-2' />
                <VirtualTable
                    data={flights}
                    mapping={[
                        {
                            name: 'Flight',
                            value: 's_flight_number'
                        }
                    ]}
                    enableClick={true}
                    handleClick={(flight) => set_s_flight_id(flight.s_flight_id)}
                    numRows={20}
                />
            </FlightsContainer>
            <TableContainer>
                <HeaderContainer>
                    <ActionIcon
                        type={'download'}
                        onClick={handleDownload}
                    />
                </HeaderContainer>
                <VirtualTable<ExtendedFFM>
                    data={renderData}
                    reactTableRef={tableRef}
                    mapping={[{
                        name: 'ULD',
                        value: 's_uld',
                        customWidth: 150
                    }, {
                        name: 'MAWB',
                        value: 's_mawb',
                        s_mawb: true
                    }, {
                        name: 'DES',
                        value: 's_destination',
                        customWidth: 50
                    }, {
                        name: 'TYPE',
                        value: 's_pieces_type',
                        customWidth: 100
                    }, {
                        name: 'Pieces',
                        value: 'i_actual_piece_count',
                        number: true,
                        customWidth: 50
                    }, {
                        name: 'Commodity',
                        value: 's_commodity',
                        customWidth: 200
                    }, {
                        name: 'Special Handling Codes',
                        value: 's_special_handling_code'
                    }, {
                        name: 'Modified',
                        value: 's_modified_by',
                        email: true
                    }, {
                        name: 'Arrival Date',
                        value: 'd_arrival_date',
                        date: true,
                        utc: true
                    }, {
                        name: 'Storage Start',
                        value: 'd_storage_start',
                        date: true,
                        utc: true
                    }, {
                        name: 'Storage Days',
                        value: 'storageDays',
                        number: true,
                        bgColor: 'yellow',
                        bgColorCondition: item => item.storageDays > 1,
                        customWidth: 100
                    }]}
                />
            </TableContainer>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex: 1;
    gap: 10px;
`;

const FlightsContainer = styled.div`
    flex-basis: 200px;
`;

const TableContainer = styled.div`
    flex: 1;
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;